import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { applyTrialistExtension, sendPurchaseEmail, markTrialistExpired } from "../services/APIClient";
import "./FreeTrialBanner.css";
import { Fallback, BigSpinner } from "../services/SpinnerComponent";

function FreeTrialBanner({ schoolid, teacherName, freeDaysLeft, trialistDetails }) {
    // Re-activate license features
    const SHOW_LICENSE_FEATURES = true;

    const [showExtendModal, setShowExtendModal] = useState(false);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    const [forwardEmail, setForwardEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Extract purchase years from trialistDetails
    const purchased_2425 = trialistDetails?.purchased_2425 || false;
    const purchased_2526 = trialistDetails?.purchased_2526 || false;
    const purchased_2627 = trialistDetails?.purchased_2627 || false;

    // Determine end date based on purchase year
    let hardEndDate;
    if (purchased_2627) {
        hardEndDate = new Date('2027-06-30');
    } else if (purchased_2526) {
        hardEndDate = new Date('2026-06-30');
    } else if (purchased_2425) {
        hardEndDate = new Date('2025-06-30');
    } else {
        hardEndDate = new Date('2025-06-30'); // Default for trial users
    }

    const currentDate = new Date();

    // Calculate days left until hard end date
    const daysUntilHardEndDate = Math.max(0, Math.ceil((hardEndDate - currentDate) / (1000 * 60 * 60 * 24)));

    // Check if we're past the hard end date
    const isPastHardEndDate = currentDate > hardEndDate;

    // Function to format dates in Irish format (DD/MM/YYYY)
    const formatIrishDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format the hard end date
    const formattedHardEndDate = formatIrishDate(hardEndDate);

    // Original pricing message for trial users
    const originalPricingMessage = (
        <>
            <span className="pricing-label">PRICING:</span>
            <br />ALPACA costs €10 per junior infant and €10 per senior infant per year for the 3 screening points.
            <br /><br /><strong>Purchase and pay pre summer</strong> for all junior infants* for 25/26 and receive your <strong>senior infants for free</strong> for 25/26 (A 50% discount).
            <br /><br />*we trust schools to self attest their intake numbers. Please enter the junior and senior numbers you know to be true at time of purchase
            <br /><br /><strong>**payment after 14 days of invoice will revert to standard pricing</strong>
        </>
    );

    // New pricing message for purchased users
    const newPricingMessage = (
        <>
            <span className="pricing-label">PRICING:</span>
            <br />ALPACA costs €10 per junior infant and €10 per senior infant per year for the 3 screening points.
            <br /><br />As a valued customer <strong>purchase and pay pre summer</strong> for all junior infants* for 25/26 and receive your <strong>senior infants for free</strong> for 25/26 (A 50% discount).
            <br /><br />*we trust schools to self attest their intake numbers. Please enter the junior and senior numbers you know to be true at time of purchase
            <br /><br /><strong>**payment after 14 days of invoice will revert to standard pricing</strong>
        </>
    );

    const handleExtendModal = () => setShowExtendModal(true);
    const handlePurchaseModal = () => setShowPurchaseModal(true);
    const handleExpiredModal = () => setShowExpiredModal(true);
    const handleModalClose = () => {
        setShowExtendModal(false);
        setShowPurchaseModal(false);
        setShowExpiredModal(false);
        setMessage("");
    };

    const handleApplyNow = async () => {
        setLoading(true);
        setMessage("");
        try {
            await applyTrialistExtension(schoolid);
            setMessage("Extension applied successfully!");
        } catch (error) {
            setMessage("Failed to apply for an extension. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleForwardSubmit = async () => {
        if (!/\S+@\S+\.\S+/.test(forwardEmail)) {
            setMessage("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setMessage("");
        try {
            await sendPurchaseEmail(forwardEmail, teacherName, purchaseFormUrl);
            setMessage("Purchase email sent successfully!");
            setForwardEmail("");
        } catch (error) {
            setMessage("Failed to send purchase email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleTrialistExpire = useCallback(async () => {
        setMessage("");
        try {
            await markTrialistExpired(schoolid);
            setMessage("Trial marked as expired.");
        } catch (error) {
            setMessage("Failed to mark trial as expired. Please try again.");
        }
    }, [schoolid]);

    useEffect(() => {
        // For trial users, check if free days left is 0
        if (!purchased_2425 && trialistDetails && freeDaysLeft === 0) {
            handleTrialistExpire();
        }

        // For purchased users, check if past hard end date
        if (SHOW_LICENSE_FEATURES && purchased_2425 && isPastHardEndDate) {
            handleTrialistExpire();
        }
    }, [freeDaysLeft, trialistDetails, handleTrialistExpire, isPastHardEndDate, purchased_2425]);

    if (!trialistDetails) {
        return <BigSpinner />;
    }

    // Hide component if user has purchased for future years
    if (purchased_2526 || purchased_2627) {
        return null;
    }

    const {
        start_date = "",
        end_date = "",
        extension_applied = false,
        completed = false,
        expired = purchased_2425 ? isPastHardEndDate : (trialistDetails?.expired || false)
    } = trialistDetails || {};

    // Select the appropriate messaging based on purchase status
    const pricingMessage = purchased_2425 ? newPricingMessage : originalPricingMessage;

    // Select the appropriate end date and days left based on purchase status
    const displayEndDate = purchased_2425 ? formattedHardEndDate : formatIrishDate(end_date);
    const displayDaysLeft = purchased_2425 ? daysUntilHardEndDate : freeDaysLeft;

    // Select the appropriate button text based on purchase status
    const purchaseButtonText = purchased_2425 ? "Summer Discount" : "Purchase Now";
    // Button text for expired users
    const expiredButtonText = purchased_2425 ? "Summer Discount" : "Purchase Now";

    // Select the appropriate purchase form URL based on purchase status
    const purchaseFormUrl = purchased_2425 
        ? "https://26983596.hs-sites-eu1.com/post-trial-form-2"  // Existing customers
        : "https://26983596.hs-sites-eu1.com/post-trial-form-2-1"; // Free trial users

    // Blue style for non-expired purchased users
    const blueButtonStyle = {
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none'
    };

    // Red style for expired purchased users
    const redButtonStyle = {
        backgroundColor: '#dc3545', // Bootstrap danger red
        color: 'white',
        border: 'none'
    };

    // Choose styling based on state
    const buttonStyle = purchased_2425 ?
        (expired ? redButtonStyle : blueButtonStyle) :
        {};

    return (
        <>
            {/* Banner */}
            <div className="free-trial-banner">
                <div className="banner-content">
                    {purchased_2425 && SHOW_LICENSE_FEATURES ? (
                        <p className="banner-message">
                            {isPastHardEndDate ?
                                "Your licence has expired. Please purchase ALPACA to continue." :
                                <><strong>{displayDaysLeft !== null ? displayDaysLeft : "..."}</strong> days left in your ALPACA Licence.</>
                            }
                        </p>
                    ) : expired ? (
                        <p className="banner-message">
                            Your free trial has expired. Please purchase ALPACA to continue.
                        </p>
                    ) : (
                        <p className="banner-message">
                            <strong>{displayDaysLeft !== null ? displayDaysLeft : "..."}</strong> days left in your free trial.
                            {extension_applied ? "" : " Do you need an extension?"}
                        </p>
                    )}
                </div>
                {!expired && (
                    <div className="banner-buttons">
                        {!purchased_2425 && (
                            <button
                                className="extend-btn"
                                onClick={handleExtendModal}
                                disabled={extension_applied || completed}
                            >
                                {extension_applied ? "Extension Applied" : "Extend Trial"}
                            </button>
                        )}
                        <button
                            className="purchase-btn"
                            onClick={handlePurchaseModal}
                            style={SHOW_LICENSE_FEATURES ? buttonStyle : {}}
                        >
                            {SHOW_LICENSE_FEATURES ? purchaseButtonText : "Purchase Now"}
                        </button>
                    </div>
                )}
                {expired && !purchased_2425 && (
                    <div className="banner-buttons">
                        <button
                            className="expired-btn"
                            onClick={handleExpiredModal}
                        >
                            {SHOW_LICENSE_FEATURES ? expiredButtonText : "Purchase Now"}
                        </button>
                    </div>
                )}
                {expired && purchased_2425 && SHOW_LICENSE_FEATURES && (
                    <div className="banner-buttons">
                        <button
                            className="expired-btn"
                            onClick={handleExpiredModal}
                            style={buttonStyle}
                        >
                            {expiredButtonText}
                        </button>
                    </div>
                )}
            </div>

            {/* Expired Modal */}
            <Modal show={showExpiredModal} onHide={handleModalClose} className="custom-expired-modal">
                <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <h2 className="modal-title">{purchased_2425 && SHOW_LICENSE_FEATURES ? "Your Licence Has Expired" : "Your Trial Has Expired"}</h2>
                    <p className="modal-subtitle">
                        {purchased_2425 && SHOW_LICENSE_FEATURES
                            ? "Thank you for your support of ALPACA. To continue using ALPACA, please complete the purchase form. Continued access will be automatic and an invoice will be sent to the school which can be paid by bank transfer or cheque."
                            : "To continue using ALPACA after the trial, please complete the purchase form. Continued access will be automatic and an invoice will be sent to the school which can be paid by bank transfer or cheque."
                        }
                    </p>
                    <p className="pricing-info">
                        {purchased_2425 && SHOW_LICENSE_FEATURES ? newPricingMessage : originalPricingMessage}
                    </p>
                </Modal.Body>
                <Modal.Footer className="custom-modal-footer">
                    <Button
                        variant="primary"
                        as="a"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={purchaseFormUrl}
                        className="purchase-btn-modal"
                    >
                        Purchase Form
                    </Button>
                    <div className="contact-support-container">
                        <Button
                            variant="light"
                            as="a"
                            href="mailto:info@alpaca-assessment.com"
                            className="contact-support-btn"
                        >
                            Contact Support
                        </Button>
                        <p className="support-email-display">info@alpaca-assessment.com</p>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Extend Trial Modal (only for non-purchased users) */}
            <Modal show={showExtendModal} onHide={handleModalClose} className="custom-extend-modal">
                <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <div className="trial-end-container">
                        <span className="trial-end-badge">Trial ends on</span>
                        <span className="trial-end-date">{displayEndDate}</span>
                    </div>
                    <h2 className="modal-title">Apply for a 1 Week extension</h2>
                    <p className="modal-subtitle">
                        If you have any questions, <a href="mailto:info@alpaca-assessment.com" className="contact-link">please let us know</a>.
                    </p>
                    {message && <p className="message">{message}</p>}
                </Modal.Body>
                <Modal.Footer className="custom-modal-footer">
                    <Button
                        variant="light"
                        className="apply-now-btn"
                        onClick={handleApplyNow}
                        disabled={loading || extension_applied}
                    >
                        {loading ? "Applying..." : "Apply now"}
                    </Button>
                    <Button
                        variant="primary"
                        as="a"
                        target="_blank"
                        href={purchaseFormUrl}
                        className="purchase-btn-modal"
                    >
                        Purchase Form
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Purchase Modal */}
            <Modal show={showPurchaseModal} onHide={handleModalClose} className="custom-purchase-modal">
                <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <div className="trial-end-container">
                        <span className="trial-end-badge">{purchased_2425 && SHOW_LICENSE_FEATURES ? "Licence ends on" : "Trial ends on"}</span>
                        <span className="trial-end-date">{displayEndDate}</span>
                    </div>
                    <h2 className="modal-title">
                        {purchased_2425 && SHOW_LICENSE_FEATURES
                            ? `${displayDaysLeft} days left in your school licence`
                            : `${displayDaysLeft} days left in your free trial`
                        }
                    </h2>
                    <p className="modal-subtitle">
                        {purchased_2425 && SHOW_LICENSE_FEATURES
                            ? "Thank you for your support of ALPACA. To continue using ALPACA, please complete the purchase form. Continued access will be automatic and an invoice will be sent to the school which can be paid by bank transfer or cheque."
                            : "To continue using ALPACA after the trial, please complete the purchase form. Continued access will be automatic and an invoice will be sent to the school which can be paid by bank transfer or cheque."
                        }
                    </p>
                    <p className="pricing-info">
                        {SHOW_LICENSE_FEATURES ? pricingMessage : originalPricingMessage}
                        <br /><span className="pricing-includes">✓ Includes all 3 screening points throughout the school year</span>
                    </p>

                    <div className="purchase-button-container">
                        <Button
                            variant="primary"
                            as="a"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={purchaseFormUrl}
                            className="purchase-btn-modal"
                            disabled={purchased_2425 && !SHOW_LICENSE_FEATURES} // Disable for purchased users if feature flag is off
                        >
                            Purchase Form
                        </Button>
                    </div>
                    <div className="forward-section">
                        <hr className="divider" />
                        <p className="forward-message">Or forward message to purchasing person</p>
                        <input
                            type="email"
                            placeholder="name@school.com"
                            value={forwardEmail}
                            onChange={(e) => setForwardEmail(e.target.value)}
                            className="form-control custom-input"
                        />
                        <Button
                            variant="primary"
                            className="forward-btn-modal"
                            onClick={handleForwardSubmit}
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Forward"}
                        </Button>
                        {message && <p className="message">{message}</p>}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default FreeTrialBanner;